import { Directive, ElementRef, OnInit, ViewChild } from '@angular/core';

import { IInputMaskConfig } from '../../../../../models/input-mask-config.model';
import { InputType } from '../models/input-type.models';
import { MASK_CONSTANTS } from '../constants/mask.constants';
import { InputMaskHelper } from '../helpers/input-mask.helper';
import { OfficeFieldWebInputTypeEnum } from '@pages/office/models/office-data.model';
import { BaseFormControlDirective } from '@pages/office/office-form/office-form/form-controls/base-form-control.directive';

@Directive()
export class MaskedInputDirective extends BaseFormControlDirective implements OnInit {
  @ViewChild('inputRef', { static: false, read: ElementRef }) inputRef: ElementRef<HTMLInputElement>;

  public type: InputType;
  public inputMaskConfig: IInputMaskConfig;

  ngOnInit(): void {
    super.ngOnInit();
    this.inputMaskConfig = this.officeField.InputMaskConfig;
  }

  public onBlur(event: MouseEvent): void {
    this.inputMaskConfig?.isMultipartMask && this.formatMultiPartIfApplicable(event?.target as HTMLInputElement);
  }

  public inputTransformFn = (value: string | number): string => {
    let result = value.toString();
    if (this.shouldCalculateMaxDigitsBeforeAndAfterDecimal()) {
      const newValue = this.enforceMaxDigitsBeforeDecimal(result);
      result = result === newValue ? value.toString() : newValue;
    }
    if (value && this.officeField?.InputMask?.includes(MASK_CONSTANTS.upperCaseMask)) {
      result = result.toUpperCase();
    }
    return result;
  };

  private isNumericMask(mask: string): boolean {
    return mask?.trim()?.startsWith('#') || mask?.trim()?.startsWith('0');
  }

  private isIntegerWebInputType(): boolean {
    const type = this.officeField.WebInputType;
    return (
      type === OfficeFieldWebInputTypeEnum.byte ||
      type === OfficeFieldWebInputTypeEnum.int ||
      type === OfficeFieldWebInputTypeEnum.long ||
      type === OfficeFieldWebInputTypeEnum.short
    );
  }

  private shouldCalculateMaxDigitsBeforeAndAfterDecimal(): boolean {
    return this.officeField?.InputMask && !this.isIntegerWebInputType() && this.isNumericMask(this.officeField?.InputMask);
  }

  // Calculate from the original Vista mask number of digits before and after decimal point
  private calcMaxDigitsBeforeAndAfterDecimal(): [number, number] {
    if (this.shouldCalculateMaxDigitsBeforeAndAfterDecimal()) {
      // Extract the part before and after the decimal marker from the Vista mask
      const [integerPart, decimalPart] = this.officeField.InputMask.split(this.inputMaskConfig?.decimalMarker || '.');

      // Count the number of mandatory digits (0's) in the integer part
      return [(integerPart?.match(/#/g) || []).length, (decimalPart?.match(/0/g) || []).length];
    }
  }

  private replaceSeparators(value: string, separators: string[], replacement = ''): string {
    if (!value || !separators?.length) {
      return value;
    }

    // Escape special characters and create a regex pattern
    const escapedSeparators = separators.map(sep => `\\${sep}`).join('');
    const regex = new RegExp(`[${escapedSeparators}]`, 'g');

    return value.replace(regex, replacement);
  }

  private enforceMaxDigitsBeforeDecimal(value: string): string {
    if (value?.length && this.shouldCalculateMaxDigitsBeforeAndAfterDecimal()) {
      const [integerPart, decimalPart] = this.replaceSeparators(
        value,
        this.officeField.InputMaskConfig?.specialCharacters?.filter(c => c !== (this.officeField.InputMaskConfig?.decimalMarker || '.'))
      ).split(this.inputMaskConfig?.decimalMarker || '.');

      const [maxDigitsBeforeDecimal, maxDigitsAfterDecimal] = this.calcMaxDigitsBeforeAndAfterDecimal();
      if (integerPart.length > maxDigitsBeforeDecimal) {
        // Enforce max digits before decimal
        const limitedIntegerPart = integerPart.slice(0, maxDigitsBeforeDecimal);
        const extraIntegerPart = integerPart.slice(maxDigitsBeforeDecimal);

        let newDecimalPart = !!decimalPart || '';
        if (extraIntegerPart && this.inputMaskConfig?.decimalMarker) {
          newDecimalPart = (extraIntegerPart + newDecimalPart).slice(0, maxDigitsAfterDecimal);
        }
        // Reconstruct the value with the decimal part untouched
        const newValue = newDecimalPart
          ? `${limitedIntegerPart}${this.inputMaskConfig?.decimalMarker || '.'}${newDecimalPart}`
          : limitedIntegerPart;

        // Update the input value
        return newValue;
      }
    }
    return value;
  }

  private formatMultiPartIfApplicable(targetInput: HTMLInputElement): void {
    if (this.inputMaskConfig?.isMultipartMask) {
      const inputElement: HTMLInputElement = targetInput || this.inputRef.nativeElement;
      if (!inputElement || !this.officeField?.InputMask) return;
      // Apply the mask formatting
      const formattedValue = InputMaskHelper.FormatMultiPart(inputElement.value, this.officeField.InputMask).trimEnd();
      if (formattedValue === inputElement.value) return; // do not set values if it is the same
      this.control.setValue(formattedValue, { emitEvent: true, onlySelf: true });
      this.control.updateValueAndValidity();
    }
  }
}
